// The root-cause analysis section of the deployment details dialog: what the
// pipeline-rca workflow wrote to the record's `rca` map, and the reviewer's
// verdict on it (RcaFeedbackControl). It re-renders as that map changes,
// because the record arrives over the live stream.

import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { RcaInfo } from '../../types/DeploymentRecord';
import { getColors } from '../theme/tokens';
import { statusHues, tagStyle } from '../theme/statusTokens';
import { formatTimestamp } from '../lib/format';
import { formatConfidence } from '../lib/rca';
import RcaFeedbackControl from './RcaFeedbackControl';

const statusLabel: Record<string, string> = {
  pending: 'Queued',
  running: 'Analysing',
  done: 'Done',
  error: 'Failed',
  skipped: 'Skipped',
};

const statusHue: Record<string, string> = {
  pending: statusHues.pending,
  running: statusHues.inprogress,
  done: statusHues.success,
  error: statusHues.failed,
  skipped: statusHues.cancelled,
};

const RcaField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  return (
    <Box>
      <Typography variant="caption" sx={{ color: c.inkMuted, display: 'block', mb: 0.25 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: c.ink, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {children}
      </Typography>
    </Box>
  );
};

const RcaPanel: React.FC<{ rca: RcaInfo; deploymentId?: string }> = ({ rca, deploymentId }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const tg = tagStyle(statusHue[rca.status] ?? statusHues.unknown, theme.palette.mode);
  const confidence = formatConfidence(rca.confidence);
  const when = rca.created_at || rca.started_at;

  return (
    <Box data-testid="rca-panel">
      <Typography variant="caption" sx={{ color: c.inkMuted, display: 'block', mb: 0.75 }}>
        Root-cause analysis
      </Typography>
      <Stack spacing={0.75}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box
            component="span"
            sx={{
              px: 1,
              py: 0.3,
              borderRadius: '6px',
              fontSize: '0.78rem',
              fontWeight: 600,
              backgroundColor: tg.bg,
              color: tg.text,
              border: `1px solid ${tg.border}`,
            }}
          >
            {statusLabel[rca.status] ?? rca.status}
          </Box>
          {rca.category && (
            <Typography variant="body2" sx={{ color: c.inkMuted }}>
              {rca.category}
            </Typography>
          )}
          {confidence && (
            <Typography variant="body2" sx={{ color: c.inkMuted }}>
              {confidence} confidence
            </Typography>
          )}
        </Box>
        {(rca.requested_by || when) && (
          <Typography variant="body2" sx={{ color: c.inkMuted }}>
            {rca.requested_by ? `Requested by ${rca.requested_by}` : ''}
            {rca.requested_by && when ? ' · ' : ''}
            {when ? formatTimestamp(when) : ''}
          </Typography>
        )}
        {rca.summary && <RcaField label="Summary">{rca.summary}</RcaField>}
        {rca.root_cause && <RcaField label="Root cause">{rca.root_cause}</RcaField>}
        {rca.next_step && <RcaField label="Next step">{rca.next_step}</RcaField>}
        {rca.error && (
          <Typography variant="body2" sx={{ color: statusHues.failed, whiteSpace: 'pre-wrap' }}>
            {rca.error}
          </Typography>
        )}
        {/* Keyed by run, so a half-filled form does not carry over to a re-run. */}
        {deploymentId && <RcaFeedbackControl key={rca.workflow_name} deploymentId={deploymentId} rca={rca} />}
      </Stack>
    </Box>
  );
};

export default RcaPanel;
